<template>
  <LcToast
    class="the-toaster-toast"
    v-bind="{ closable, tracking }"
    :headline="toastHeadline"
    :variant="type"
    @close="emit('close', id)"
  >
    <p v-if="typeof message === 'string' || typeof message === 'function'">
      {{ callIfFn(message) }}
    </p>

    <p v-for="(text, index) in message" v-else-if="Array.isArray(message)" :key="index">
      {{ callIfFn(text) }}
    </p>

    <i18n-t v-else :keypath="messageKey" :locale="message.locale" scope="global" tag="div">
      <template
        v-for="(translationVariable, slotName) in message.translationVariables"
        :key="slotName"
        #[slotName]
      >
        <HyperLink
          v-if="isLink(translationVariable)"
          :target="translationVariable.target"
          :to="
            typeof translationVariable.to === 'function'
              ? $localePath(translationVariable.to())
              : $localePath(translationVariable.to)
          "
          @click="handleClick"
        >
          {{ callIfFn(translationVariable.text) }}
        </HyperLink>

        <p v-else>
          {{ callIfFn(translationVariable.text) }}
        </p>
      </template>
    </i18n-t>
  </LcToast>
</template>

<script lang="ts" setup>
import type { LcToastVariant } from '@lottocom/frontend-components'

import type { TrackingImpressionProps } from '#tracking/app/types/tracking'
import type { RouteLocationRaw } from '#vue-router'

export type ToasterToastProps = TrackingImpressionProps & {
  autoClose?: boolean
  closable?: boolean
  headline?: (() => string) | string
  id: string
  message: (() => string) | string | string[] | ToastMessage
  onLinkClicked?: () => Promise<void> | void
  type?: LcToastVariant
}

export type ToastTranslationLink = {
  target?: '_blank' | '_parent' | '_self' | '_top'
  text: (() => string) | string
  to: (() => RouteLocationRaw | string) | RouteLocationRaw
  type: 'link'
}

export type ToastTranslationText = {
  text: (() => string) | string
  type: 'text'
}

type ToastMessage = {
  headline?: string
  locale?: (typeof availableLocales)[number]
  translationKey: (() => string) | string
  translationVariables?: ToastMessageTranslationVariables
}

type ToastMessageTranslationVariables = Record<string, ToastTranslationType>

type ToastTranslationType = ToastTranslationLink | ToastTranslationText

const emit = defineEmits<{
  (e: 'close', id: string): void
}>()

const {
  autoClose = true,
  closable = true,
  headline,
  id,
  message,
  onLinkClicked,
} = defineProps<ToasterToastProps>()

// oxlint-disable-next-line no-unused-vars
const { availableLocales } = useI18n()

const callIfFn = (a: (() => string) | string) => (typeof a === 'string' ? a : a())

const toastHeadline = computed(() => {
  if (!headline) {
    return typeof message === 'object' && 'headline' in message ? message.headline : undefined
  }

  return callIfFn(headline)
})

const messageKey = computed(() => {
  switch (typeof message) {
    case 'function':
      return message()
    case 'string':
      return message
    default:
      if (Array.isArray(message)) {
        return message[0]
      }

      return typeof message.translationKey === 'function'
        ? message.translationKey()
        : message.translationKey
  }
})

const isLink = (
  translationVariable: ToastTranslationType,
): translationVariable is ToastTranslationLink => translationVariable.type === 'link'

const handleClick = async () => {
  if (onLinkClicked) await onLinkClicked()

  emit('close', id)
}

onMounted(() => {
  if (!autoClose) return

  setTimeout(() => {
    emit('close', id)
  }, 5000)
})
</script>

<style lang="scss" scoped>
.the-toaster-toast {
  a {
    @include link-default;
  }
}
</style>
